<template>
  <div class="mp-root">
    <div class="mp-page">
      <!-- Tab bar -->
      <div class="tab-bar">
        <button
          class="tab"
          :class="{ active: activeTab === 'my' }"
          @click="uTab('my')"
        >
          {{ $t('MPTabMy') }}
        </button>
        <button
          class="tab"
          :class="{ active: activeTab === 'all' }"
          @click="uTab('all')"
        >
          {{ $t('MPTabAll') }}
        </button>
        <button
          class="tab"
          :class="{ active: activeTab === 'tarix' }"
          @click="uTab('tarix')"
        >
          <i class="far fa-clock" style="margin-right: 5px"></i>{{ $t('MPTabHistory') }}
        </button>
      </div>

      <!-- MENING MAHALLAM -->
      <div v-show="activeTab === 'my' && !chatOpen" class="view">
        <div v-if="!myMahalla" class="my-empty">
          <i class="fas fa-map-marker-alt my-empty-ico"></i>
          <h3>{{ $t('MPMyMahallaLabel') }}</h3>
          <p>{{ $t('MPMyMahallaSelectHint') }}</p>
          <button class="btn btn-p my-select-btn" @click="openMyMahallaModal('create')">
            <i class="fas fa-plus" style="margin-right: 6px"></i>
            {{ $t('MPMyMahallaSelect') }}
          </button>
        </div>

        <div v-else>
          <div class="my-saved-head">
            <div class="my-saved-info">
              <div class="my-saved-label">{{ $t('MPMyMahallaLabel') }}</div>
              <div class="my-saved-title">{{ myMahalla.mfyText }}</div>
              <div class="my-saved-sub">
                {{ myMahalla.regionText }} · {{ myMahalla.districtText }}
              </div>
            </div>
            <button class="my-edit-btn" @click="openMyMahallaModal('edit')" :title="$t('MPMyMahallaEdit')">
              <i class="fas fa-pencil-alt"></i>
            </button>
          </div>

          <div v-if="loadingMyMembers" class="empty">{{ $t('MPLoading') }}</div>
          <template v-else-if="myMembers.length">
            <p class="slabel">{{ $t('MPMembersLabel') }}</p>
            <person-card
              v-for="emp in myMembers"
              :key="emp.id"
              :name="emp.fullName"
              :role="emp.positionName"
              @appeal="openChat(emp.fullName, emp.positionName, { employeeId: emp.id, regionId: myMahalla.regionId, districtId: myMahalla.districtId, mfyId: myMahalla.mfyId, regionText: myMahalla.regionText, districtText: myMahalla.districtText, mfyText: myMahalla.mfyText })"
            />
          </template>
          <div v-else class="empty">{{ $t('MPNoMembers') }}</div>
        </div>
      </div>

      <!-- BARCHASI -->
      <div v-show="activeTab === 'all' && !chatOpen" class="view">
        <div v-show="drillStep === 'v'">
          <div class="mp-sticky-head">
            <div class="ph"><h2>{{ $t('MPSelectRegion') }}</h2></div>
            <div class="mp-search">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchRegion"
                :placeholder="$t('MPSearchPlaceholder')"
              />
              <i
                v-if="searchRegion"
                class="fas fa-times mp-search-clear"
                @click="searchRegion = ''"
              ></i>
            </div>
          </div>
          <div v-if="loadingRegions" class="empty">{{ $t('MPLoading') }}</div>
          <div v-else-if="!regions.length" class="empty">{{ $t('MPNoRegions') }}</div>
          <div v-else-if="!filteredRegions.length" class="empty">{{ $t('MPSearchNoResults') }}</div>
          <div v-else class="slist">
            <div
              v-for="(r, i) in filteredRegions"
              :key="r.value"
              class="sitem"
              @click="selectRegion(r)"
            >
              <div class="si-ico" :class="iconClass(i)"><i class="fas fa-globe"></i></div>
              <div>
                <div class="si-n">{{ r.text }}</div>
              </div>
              <i class="fas fa-chevron-right si-arr" style="margin-left: auto"></i>
            </div>
          </div>
        </div>

        <div v-show="drillStep === 't'">
          <div class="mp-sticky-head">
            <div class="bc">
              <a href="#" @click.prevent="drill('v')">{{ $t('MPRegions') }}</a>
              
              <span>{{ selectedRegion && selectedRegion.text }}</span>
            </div>
            <div class="ph"><h2>{{ $t('MPSelectDistrict') }}</h2></div>
            <div class="mp-search">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchDistrict"
                :placeholder="$t('MPSearchPlaceholder')"
              />
              <i
                v-if="searchDistrict"
                class="fas fa-times mp-search-clear"
                @click="searchDistrict = ''"
              ></i>
            </div>
          </div>
          <div v-if="loadingDistricts" class="empty">{{ $t('MPLoading') }}</div>
          <div v-else-if="!districts.length" class="empty">{{ $t('MPNoDistricts') }}</div>
          <div v-else-if="!filteredDistricts.length" class="empty">{{ $t('MPSearchNoResults') }}</div>
          <div v-else class="slist">
            <div
              v-for="d in filteredDistricts"
              :key="d.value"
              class="sitem"
              @click="selectDistrict(d)"
            >
              <div class="si-ico ic-gr"><i class="fas fa-desktop"></i></div>
              <div>
                <div class="si-n">{{ d.text }}</div>
              </div>
              <i class="fas fa-chevron-right si-arr" style="margin-left: auto"></i>
            </div>
          </div>
        </div>

        <div v-show="drillStep === 'm'">
          <div class="mp-sticky-head">
            <div class="bc">
              <a href="#" @click.prevent="drill('v')">{{ $t('MPRegions') }}</a>
              
              <a href="#" @click.prevent="drill('t')">{{ selectedRegion && selectedRegion.text }}</a>
              
              <span>{{ selectedDistrict && selectedDistrict.text }}</span>
            </div>
            <div class="ph"><h2>{{ $t('MPSelectMahalla') }}</h2></div>
            <div class="mp-search">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchMahalla"
                :placeholder="$t('MPSearchPlaceholder')"
              />
              <i
                v-if="searchMahalla"
                class="fas fa-times mp-search-clear"
                @click="searchMahalla = ''"
              ></i>
            </div>
          </div>
          <div v-if="loadingMahallas" class="empty">{{ $t('MPLoading') }}</div>
          <div v-else-if="!mahallas.length" class="empty">{{ $t('MPNoMahallas') }}</div>
          <div v-else-if="!filteredMahallas.length" class="empty">{{ $t('MPSearchNoResults') }}</div>
          <div v-else class="slist">
            <div
              v-for="m in filteredMahallas"
              :key="m.value"
              class="sitem"
              @click="selectMahalla(m)"
            >
              <div class="si-ico ic-g"><i class="fas fa-home"></i></div>
              <div>
                <div class="si-n">{{ m.text }}</div>
              </div>
              <button
                class="si-sync"
                :disabled="syncingMfyId === m.value"
                :title="$t('MPSyncEmployees') || 'Xodimlarni sinxronlash'"
                @click.stop="syncMfyEmployees(m)"
              >
                <i
                  class="fas"
                  :class="syncingMfyId === m.value ? 'fa-spinner fa-spin' : 'fa-sync-alt'"
                ></i>
              </button>
              <i class="fas fa-chevron-right si-arr"></i>
            </div>
          </div>
        </div>

        <div v-show="drillStep === 'mem'">
          <div class="bc">
            <a href="#" @click.prevent="drill('v')">{{ $t('MPRegions') }}</a>
            
            <a href="#" @click.prevent="drill('t')">{{ selectedRegion && selectedRegion.text }}</a>
            
            <a href="#" @click.prevent="drill('m')">{{ selectedDistrict && selectedDistrict.text }}</a>
            
            <span>{{ selectedMahalla && selectedMahalla.text }}</span>
          </div>
          <div v-if="loadingMembers" class="empty">{{ $t('MPLoading') }}</div>
          <div v-else-if="!memMembers.length" class="empty">{{ $t('MPNoMembers') }}</div>
          <template v-else>
            <p class="slabel">{{ $t('MPMembersLabel') }}</p>
            <person-card
              v-for="emp in memMembers"
              :key="emp.id"
              :name="emp.fullName"
              :role="emp.positionName"
              @appeal="openChat(emp.fullName, emp.positionName, { employeeId: emp.id, regionId: selectedRegion ? selectedRegion.value : 0, districtId: selectedDistrict ? selectedDistrict.value : 0, mfyId: selectedMahalla ? selectedMahalla.value : 0, regionText: selectedRegion ? selectedRegion.text : '', districtText: selectedDistrict ? selectedDistrict.text : '', mfyText: selectedMahalla ? selectedMahalla.text : '' })"
            />
          </template>
        </div>
      </div>

      <!-- TARIX -->
      <div v-show="activeTab === 'tarix' && !chatOpen" class="view">
        <div v-if="!ticketDetail">
          <div class="ph">
            <h2>{{ $t('MPHistoryTitle') }}</h2>
            <p>{{ $t('MPHistorySubtitle') }}</p>
          </div>
          <div class="tarix-search">
            <div class="mp-search">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="ticketSearch"
                :placeholder="$t('MPSearchAppealPlaceholder')"
              />
              <i
                v-if="ticketSearch"
                class="fas fa-times mp-search-clear"
                @click="ticketSearch = ''"
              ></i>
            </div>
          </div>
          <div class="tarix-tabs">
            <button
              v-for="f in tarixTabs"
              :key="f.code"
              class="tab"
              :class="{ active: activeFilter === f.code }"
              @click="setTypeFilter(f.code)"
            >
              {{ f.label }}
            </button>
          </div>
          <p class="tarix-head">{{ $t('MPAppealCount', { count: filteredTickets.length }) }}</p>

          <div v-if="loadingTickets" class="empty">{{ $t('MPLoading') }}</div>
          <div v-else-if="!filteredTickets.length" class="empty">{{ $t('MPSearchNoResults') }}</div>

          <div
            v-for="t in filteredTickets"
            :key="t.id || t.rawId"
            class="tcard"
            :class="{ 'has-reply': t.hasReply }"
            @click="openTicketDetail(t)"
          >
            <div class="tcard-top">
              <span class="tcard-date">{{ t.date }}</span>
              <span class="st-pill" :class="t.hasReply ? 'st-replied' : 'st-sent'">
                {{ t.statusName || (t.hasReply ? $t('MPStatusReplied') : $t('MPStatusSent')) }}
              </span>
            </div>
            <div class="tcard-type">{{ t.typeName || typeLabel(t.type) }}</div>

            <div class="tcard-info">
              <div v-if="ticketLocation(t)" class="tcard-info-row">
                <i class="fas fa-map-marker-alt"></i>
                <span>{{ ticketLocation(t) }}</span>
              </div>
              <div v-if="t.recipientName" class="tcard-info-row">
                <i class="fas fa-user-tie"></i>
                <span>{{ t.recipientName }}</span>
                <span v-if="t.recipientPosition" class="tcard-position">{{ t.recipientPosition }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Ticket detail -->
        <div v-else>
          <div class="bc" style="margin-bottom: 14px">
            <a href="#" @click.prevent="closeTicketDetail">
              <i class="fas fa-arrow-left" style="margin-right: 4px"></i>{{ $t('MPBack') }}
            </a>
          </div>
          <div v-if="loadingTicketDetail" class="empty">{{ $t('MPLoading') }}</div>
          <template v-else>
          <div class="info-table">
            <div class="info-row">
              <span class="info-key">{{ $t('MPAppealNumber') }}</span>
              <span class="info-val" style="font-family: 'Nunito', sans-serif">{{ ticketDetail.id }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">{{ $t('MPType') }}</span>
              <span class="info-val">{{ ticketDetail.typeName || typeLabel(ticketDetail.type) }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">{{ $t('MPDate') }}</span>
              <span class="info-val">{{ ticketDetail.date }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">{{ $t('MPStatus') }}</span>
              <span class="info-val">
                <span
                  class="st-pill"
                  :class="ticketDetail.hasReply ? 'st-replied' : 'st-sent'"
                  style="font-size: 13px"
                >
                  {{ ticketDetail.statusName || (ticketDetail.hasReply ? $t('MPStatusReplied') : $t('MPStatusSent')) }}
                </span>
              </span>
            </div>
            <div v-if="ticketDetail.phoneNumber" class="info-row">
              <span class="info-key">{{ $t('MPPhoneLabel') }}</span>
              <span class="info-val" style="font-family: 'Nunito', sans-serif">{{ ticketDetail.phoneNumber }}</span>
            </div>
            <div v-if="ticketDetailLocation" class="info-row">
              <span class="info-key">{{ $t('MPLocation') }}</span>
              <span class="info-val">{{ ticketDetailLocation }}</span>
            </div>
            <div v-if="ticketDetail.recipientName" class="info-row">
              <span class="info-key">{{ $t('MPRecipient') }}</span>
              <span class="info-val">{{ ticketDetail.recipientName }}</span>
            </div>
            <div v-if="ticketDetail.recipientPosition" class="info-row">
              <span class="info-key">{{ $t('MPPosition') }}</span>
              <span class="info-val">{{ ticketDetail.recipientPosition }}</span>
            </div>
          </div>

          <div v-if="ticketDetail.text" class="td-section">
            <div class="td-section-title">{{ $t('MPRequestTextTitle') }}</div>
            <div class="td-section-text">{{ ticketDetail.text }}</div>
          </div>

          <div v-if="ticketDetail.files && ticketDetail.files.length" class="td-section">
            <div class="td-section-title">{{ $t('MPFileLabel') }}</div>
            <div class="td-section-row">
              <button
                v-for="(f, idx) in ticketDetail.files"
                :key="(f.id || idx) + '-req'"
                class="td-dl-btn"
                @click="downloadFile(f)"
              >
                <i class="fas fa-download"></i>
                {{ $t('MPDownload') }}
              </button>
            </div>
          </div>

          <div v-if="ticketDetail.replyFiles && ticketDetail.replyFiles.length" class="td-section">
            <div class="td-section-title">{{ $t('MPReplyFileTitle') }}</div>
            <div class="td-section-row">
              <button
                v-for="(f, idx) in ticketDetail.replyFiles"
                :key="(f.id || idx) + '-rep'"
                class="td-dl-btn"
                @click="downloadFile(f)"
              >
                <i class="fas fa-download"></i>
                {{ $t('MPDownload') }}
              </button>
            </div>
          </div>
          </template>
        </div>
      </div>

      <!-- CHAT VIEW -->
      <div v-show="chatOpen" class="view">
        <div class="bc" style="margin-bottom: 12px">
          <a href="#" @click.prevent="closeChat">
            <i class="fas fa-arrow-left" style="margin-right: 4px"></i>{{ $t('MPBack') }}
          </a>
          
          <span>{{ chatRecipient.name }}</span>
        </div>
        <div class="chat-page">
          <div class="chat-header">
            <div class="chat-hav">
              <img
                :src="chatRecipient.photo || emptyAvatar"
                alt=""
                class="chat-hav-img"
                @error="onPhotoError"
              />
            </div>
            <div class="chat-hinfo">
              <div class="chat-htitle">{{ chatRecipient.name }}</div>
              <div class="chat-hsub">{{ chatRecipient.role || $t('MPChatSubtitle') }}</div>
              <div v-if="chatRecipient.regionText || chatRecipient.districtText || chatRecipient.mfyText" class="chat-hloc">
                <i class="fas fa-map-marker-alt"></i>
                <span>{{ recipientLocation }}</span>
              </div>
            </div>
          </div>

          <div class="chat-body" ref="chatBody">
            <div v-for="(msg, idx) in chatMessages" :key="idx">
              <!-- bot welcome with type chips -->
              <div v-if="msg.kind === 'welcome'" class="msg">
                <div>
                  <div class="bbl">
                    {{ $t('MPWelcome') }}
                    <div v-if="!chatState.type" class="mtype-row">
                      <button
                        v-for="t in murojaatTypes"
                        :key="t.code"
                        class="mtype-chip"
                        @click="selectType(t.code)"
                      >
                        {{ t.label }}
                      </button>
                    </div>
                  </div>
                  <div class="msg-time">{{ msg.time }}</div>
                </div>
              </div>

              <!-- plain text bubble -->
              <div v-else-if="msg.kind === 'text'" class="msg" :class="{ me: msg.me }">
                <div>
                  <div class="bbl" :class="{ me: msg.me }">{{ msg.text }}</div>
                  <div class="msg-time" :class="{ me: msg.me }">{{ msg.time }}</div>
                </div>
              </div>

              <!-- bot "type selected" message -->
              <div v-else-if="msg.kind === 'typed'" class="msg">
                <div>
                  <div class="bbl">
                    {{ $t('MPSelectedType') }} <b>{{ typeLabel(msg.type) }}</b>.<br />
                    {{ $t('MPWriteText') }}
                  </div>
                  <div class="msg-time">{{ msg.time }}</div>
                </div>
              </div>

              <!-- bot confirmation summary -->
              <div v-else-if="msg.kind === 'confirmInfo'" class="msg">
                <div>
                  <div class="bbl">
                    {{ $t('MPConfirm') }}<br />
                    <b>{{ $t('MPTypeLabel') }}</b> {{ typeLabel(msg.type) }}<br />
                    <b>{{ $t('MPPhoneLabel') }}</b> {{ msg.phone }}<br />
                    <b>{{ $t('MPTextLabel') }}</b> {{ msg.text }}
                    <template v-if="msg.fileName">
                      <br /><b>{{ $t('MPFileLabel') }}</b> {{ msg.fileName }}
                    </template>
                  </div>
                  <div class="msg-time">{{ msg.time }}</div>
                </div>
              </div>

              <!-- system message -->
              <div v-else-if="msg.kind === 'sys'">
                <div class="sys-msg">{{ msg.text }}</div>
              </div>

              <!-- confirm buttons -->
              <div v-else-if="msg.kind === 'confirm'" class="msg">
                <div>
                  <div style="display: flex; gap: 9px; flex-wrap: wrap">
                    <button
                      class="btn btn-p btn-sm"
                      style="flex: none; width: auto"
                      @click="confirmSend"
                    >
                      <i class="fas fa-check" style="font-size: 12px"></i>{{ $t('MPYesSend') }}
                    </button>
                    <button
                      class="btn btn-o btn-sm"
                      style="flex: none; width: auto"
                      @click="cancelConfirm"
                    >
                      <i class="fas fa-times" style="font-size: 12px"></i>{{ $t('MPCancel') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- ticket card -->
              <div v-else-if="msg.kind === 'ticket'" class="msg me">
                <div>
                  <div class="tchat-card">
                    <div class="tchat-id">{{ msg.id }}</div>
                    <div class="tchat-meta">{{ typeLabel(msg.type) }} · {{ msg.date }}</div>
                    <div class="tchat-status">{{ $t('MPStatusNew') }}</div>
                    <a class="tchat-link" @click="uTab('tarix')">
                      <i class="fas fa-external-link-alt" style="font-size: 12px"></i>
                      {{ $t('MPViewInHistory') }}
                    </a>
                  </div>
                  <div class="msg-time me">{{ msg.time }}</div>
                </div>
              </div>

              <!-- new murojaat button -->
              <div v-else-if="msg.kind === 'newbtn'" class="msg">
                <div>
                  <button class="new-mrj-btn" @click="resetChat">
                    <i class="fas fa-plus" style="font-size: 12px"></i>{{ $t('MPNewAppeal') }}
                  </button>
                </div>
              </div>

              <!-- file upload confirm prompt -->
              <div v-else-if="msg.kind === 'fileConfirm'" class="msg">
                <div>
                  <div class="bbl">{{ $t('MPUploadFilePrompt') }}</div>
                  <div style="display: flex; gap: 9px; flex-wrap: wrap; margin-top: 8px">
                    <button
                      class="btn btn-p btn-sm"
                      style="flex: none; width: auto"
                      @click="confirmFileUpload"
                    >
                      <i class="fas fa-check" style="font-size: 12px"></i>{{ $t('MPYes') }}
                    </button>
                    <button
                      class="btn btn-o btn-sm"
                      style="flex: none; width: auto"
                      @click="cancelFileUpload"
                    >
                      <i class="fas fa-times" style="font-size: 12px"></i>{{ $t('MPNo') }}
                    </button>
                  </div>
                  <div class="msg-time">{{ msg.time }}</div>
                </div>
              </div>

              <!-- file attachment message -->
              <div v-else-if="msg.kind === 'file'" class="msg me">
                <div>
                  <div class="bbl me file-bbl">
                    <i class="fas fa-paperclip" style="margin-right: 8px"></i>
                    <span class="file-name">{{ msg.fileName }}</span>
                    <span class="file-size">{{ msg.fileSize }}</span>
                  </div>
                  <div class="msg-time me">{{ msg.time }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="chat-input-bar">
            <input
              ref="fileInput"
              type="file"
              style="display: none"
              @change="onFileSelected"
            />
            <textarea
              ref="chatInput"
              class="cinput"
              v-model="chatInputText"
              :placeholder="chatState.step === 'phone' ? '998xxxxxxxxx' : $t('MPInputPlaceholder')"
              :inputmode="chatState.step === 'phone' ? 'numeric' : 'text'"
              :maxlength="chatState.step === 'phone' ? 12 : -1"
              rows="1"
              @input="onChatInput"
              @keydown="chatKey"
            ></textarea>
            <button class="sbtn" @click="chatSend">
              <i class="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MY MAHALLA MODAL -->
    <div v-if="myModal.show" class="mp-modal-backdrop" @click.self="closeMyMahallaModal">
      <div class="mp-modal">
        <div class="mp-modal-head">
          <h3>
            {{ myModal.mode === 'edit' ? $t('MPMyMahallaEdit') : $t('MPMyMahallaSelect') }}
          </h3>
          <button class="mp-modal-x" @click="closeMyMahallaModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="mp-modal-body">
          <div class="mp-fld">
            <label class="mp-fld-lbl">{{ $t('MPRegion') }}</label>
            <select class="mp-fld-sel" v-model="myModal.regionId" @change="onMyRegionChange">
              <option :value="0">{{ $t('MPSelectRegion') }}</option>
              <option v-for="r in regions" :key="'mr-' + r.value" :value="r.value">{{ r.text }}</option>
            </select>
          </div>

          <div class="mp-fld">
            <label class="mp-fld-lbl">{{ $t('MPDistrict') }}</label>
            <select
              class="mp-fld-sel"
              v-model="myModal.districtId"
              :disabled="!myModal.regionId || myModal.loadingDistricts"
              @change="onMyDistrictChange"
            >
              <option :value="0">{{ $t('MPSelectDistrict') }}</option>
              <option v-for="d in myModal.districts" :key="'md-' + d.value" :value="d.value">{{ d.text }}</option>
            </select>
          </div>

          <div class="mp-fld">
            <label class="mp-fld-lbl">{{ $t('MPMahalla') }}</label>
            <select
              class="mp-fld-sel"
              v-model="myModal.mfyId"
              :disabled="!myModal.districtId || myModal.loadingMfys"
            >
              <option :value="0">{{ $t('MPSelectMahalla') }}</option>
              <option v-for="m in myModal.mfys" :key="'mm-' + m.value" :value="m.value">{{ m.text }}</option>
            </select>
          </div>

          <div v-if="myModal.error" class="mp-modal-err">
            <i class="fas fa-exclamation-circle"></i> {{ myModal.error }}
          </div>
        </div>
        <div class="mp-modal-foot">
          <button class="btn btn-o" :disabled="myModal.saving" @click="closeMyMahallaModal">
            {{ $t('MPCancel') }}
          </button>
          <button class="btn btn-p" :disabled="!myModal.mfyId || myModal.saving" @click="saveMyMahalla">
            <i v-if="myModal.saving" class="fas fa-spinner fa-spin" style="margin-right: 6px"></i>
            {{ $t('MPSave') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ManualService from "@/services/manual.service";
import MahallaEmployeeService from "@/services/mahallaemployee.service";
import MahallaRequestService from "@/services/mahallarequest.service";
import ContractorService from "@/services/contractor.service";
import PersonCard from "./personCard.vue";
export default {
  name: "MahallaPortal",
  components: { PersonCard },
  data() {
    return {
      activeTab: "all",
      drillStep: "v",
      activeFilter: "all",
      ticketSearch: "",
      searchRegion: "",
      searchDistrict: "",
      searchMahalla: "",
      memMembers: [],
      loadingMembers: false,
      emptyAvatar: "images/design/empty_avatar.svg",
      regions: [],
      districts: [],
      mahallas: [],
      selectedRegion: null,
      selectedDistrict: null,
      selectedMahalla: null,
      loadingRegions: false,
      loadingDistricts: false,
      loadingMahallas: false,
      syncingMfyId: 0,
      regionIconClasses: ["ic-b", "ic-a", "ic-t", "ic-p", "ic-c", "ic-gr", "ic-g"],
      tickets: [],
      loadingTickets: false,
      ticketsFilter: {
        search: "",
        sortBy: "",
        orderType: "",
        page: 1,
        pageSize: 50,
      },
      ticketDetail: null,
      loadingTicketDetail: false,
      chatOpen: false,
      chatReturnTab: "my",
      chatRecipient: {
        name: "",
        role: "",
        photo: "",
        employeeId: 0,
        regionId: 0,
        districtId: 0,
        mfyId: 0,
        regionText: "",
        districtText: "",
        mfyText: "",
      },
      chatMessages: [],
      chatInputText: "",
      chatState: {
        type: null,
        phone: "",
        text: "",
        fileName: "",
        fileId: "",
        step: "type",
      },
      sendingRequest: false,

      myMahalla: null,
      myMembers: [],
      loadingMyMembers: false,
      myModal: {
        show: false,
        mode: "create",
        regionId: 0,
        districtId: 0,
        mfyId: 0,
        districts: [],
        mfys: [],
        loadingDistricts: false,
        loadingMfys: false,
        saving: false,
        error: "",
      },
    };
  },
  computed: {
    tarixTabs() {
      return [
        { code: "all", label: this.$t("MPFilterAll") },
        { code: "complaint", label: this.$t("MPTypeComplaint") },
        { code: "suggestion", label: this.$t("MPTypeSuggestion") },
        { code: "application", label: this.$t("MPTypeApplication") },
      ];
    },
    murojaatTypes() {
      return [
        { code: "suggestion", label: this.$t("MPTypeSuggestion") },
        { code: "complaint", label: this.$t("MPTypeComplaint") },
        { code: "application", label: this.$t("MPTypeApplication") },
      ];
    },
    filteredRegions() {
      return this.filterByText(this.regions, this.searchRegion);
    },
    filteredDistricts() {
      return this.filterByText(this.districts, this.searchDistrict);
    },
    filteredMahallas() {
      return this.filterByText(this.mahallas, this.searchMahalla);
    },
    filteredTickets() {
      let list = this.tickets;
      const q = (this.ticketSearch || "").trim().toLowerCase();
      if (q) {
        list = list.filter((t) => {
          const id = (t.id || "").toLowerCase();
          const text = (t.text || "").toLowerCase();
          return id.includes(q) || text.includes(q);
        });
      }
      return list;
    },
    ticketDetailLocation() {
      if (!this.ticketDetail) return "";
      const parts = [];
      if (this.ticketDetail.regionText) parts.push(this.ticketDetail.regionText);
      if (this.ticketDetail.districtText) parts.push(this.ticketDetail.districtText);
      if (this.ticketDetail.mfyText) parts.push(this.ticketDetail.mfyText);
      return parts.join(", ");
    },
    recipientLocation() {
      const parts = [];
      if (this.chatRecipient.regionText) parts.push(this.chatRecipient.regionText);
      if (this.chatRecipient.districtText) parts.push(this.chatRecipient.districtText);
      if (this.chatRecipient.mfyText) parts.push(this.chatRecipient.mfyText);
      return parts.join(", ");
    },
  },
  created() {
    this.loadRegions();
    this.loadMyMahallaFromStorage();
    if (this.myMahalla) {
      this.activeTab = "my";
      this.loadMyMembers();
    }
  },
  methods: {
    nowTime() {
      const d = new Date();
      return (
        d.getHours().toString().padStart(2, "0") +
        ":" +
        d.getMinutes().toString().padStart(2, "0")
      );
    },
    uTab(t) {
      this.activeTab = t;
      this.chatOpen = false;
      if (t === "tarix") {
        this.ticketDetail = null;
        this.loadTickets();
      }
      if (t === "all" && !this.regions.length && !this.loadingRegions) {
        this.loadRegions();
      }
      if (t === "my" && this.myMahalla && !this.myMembers.length && !this.loadingMyMembers) {
        this.loadMyMembers();
      }
    },
    loadMyMahallaFromStorage() {
      // Prefer server-side value from user_info.contractor.appMfyId; fall back to localStorage cache.
      try {
        const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
        const c = userInfo.contractor || {};
        if (c.appMfyId) {
          this.myMahalla = {
            regionId: c.appRegionId || 0,
            regionText: c.appRegion || "",
            districtId: c.appDistrictId || 0,
            districtText: c.appDistrict || "",
            mfyId: c.appMfyId,
            mfyText: c.appMfy || "",
          };
          localStorage.setItem("my_mahalla", JSON.stringify(this.myMahalla));
          return;
        }
      } catch (e) { /* ignore */ }
      try {
        const raw = localStorage.getItem("my_mahalla");
        this.myMahalla = raw ? JSON.parse(raw) : null;
      } catch (e) {
        this.myMahalla = null;
      }
    },
    getUserContractorLocation() {
      try {
        const ui = JSON.parse(localStorage.getItem("user_info") || "{}");
        const c = ui.contractor || {};
        return {
          regionId: c.regionId || 0,
          districtId: c.districtId || 0,
        };
      } catch (e) {
        return { regionId: 0, districtId: 0 };
      }
    },
    openMyMahallaModal(mode) {
      this.myModal.mode = mode;
      if (mode === "edit" && this.myMahalla) {
        this.myModal.regionId = this.myMahalla.regionId;
        this.myModal.districtId = this.myMahalla.districtId;
        this.myModal.mfyId = this.myMahalla.mfyId;
      } else {
        const loc = this.getUserContractorLocation();
        this.myModal.regionId = loc.regionId;
        this.myModal.districtId = loc.districtId;
        this.myModal.mfyId = 0;
      }
      this.myModal.districts = [];
      this.myModal.mfys = [];
      this.myModal.error = "";
      this.myModal.saving = false;
      this.myModal.show = true;
      if (!this.regions.length && !this.loadingRegions) {
        this.loadRegions();
      }
      if (this.myModal.regionId) {
        this.loadMyDistricts(this.myModal.regionId).then(() => {
          if (this.myModal.districtId) {
            this.loadMyMfys(this.myModal.regionId, this.myModal.districtId);
          }
        });
      }
    },
    closeMyMahallaModal() {
      this.myModal.show = false;
    },
    onMyRegionChange() {
      this.myModal.districtId = 0;
      this.myModal.mfyId = 0;
      this.myModal.districts = [];
      this.myModal.mfys = [];
      if (this.myModal.regionId) {
        this.loadMyDistricts(this.myModal.regionId);
      }
    },
    onMyDistrictChange() {
      this.myModal.mfyId = 0;
      this.myModal.mfys = [];
      if (this.myModal.regionId && this.myModal.districtId) {
        this.loadMyMfys(this.myModal.regionId, this.myModal.districtId);
      }
    },
    loadMyDistricts(regionId) {
      this.myModal.loadingDistricts = true;
      return ManualService.DistrictSelectList(regionId)
        .then((res) => {
          this.myModal.districts = res.data || [];
        })
        .finally(() => {
          this.myModal.loadingDistricts = false;
        });
    },
    loadMyMfys(regionId, districtId) {
      this.myModal.loadingMfys = true;
      return ManualService.MfySelectList(regionId, districtId)
        .then((res) => {
          this.myModal.mfys = res.data || [];
        })
        .finally(() => {
          this.myModal.loadingMfys = false;
        });
    },
    saveMyMahalla() {
      if (!this.myModal.mfyId) return;
      if (this.myModal.saving) return;
      if (this._saveMyMahallaInflight) return;
      this._saveMyMahallaInflight = true;
      this.myModal.saving = true;
      this.myModal.error = "";

      const region = this.regions.find((r) => r.value === this.myModal.regionId);
      const district = this.myModal.districts.find((d) => d.value === this.myModal.districtId);
      const mfy = this.myModal.mfys.find((m) => m.value === this.myModal.mfyId);
      const next = {
        regionId: this.myModal.regionId,
        regionText: region ? region.text : "",
        districtId: this.myModal.districtId,
        districtText: district ? district.text : "",
        mfyId: this.myModal.mfyId,
        mfyText: mfy ? mfy.text : "",
      };

      ContractorService.UpdateAppLocation({
        appRegionId: next.regionId,
        appDistrictId: next.districtId,
        appMfyId: next.mfyId,
      })
        .then(() => {
          this.myMahalla = next;
          localStorage.setItem("my_mahalla", JSON.stringify(this.myMahalla));
          try {
            const ui = JSON.parse(localStorage.getItem("user_info") || "{}");
            if (ui.contractor) {
              ui.contractor.appRegionId = next.regionId;
              ui.contractor.appRegion = next.regionText;
              ui.contractor.appDistrictId = next.districtId;
              ui.contractor.appDistrict = next.districtText;
              ui.contractor.appMfyId = next.mfyId;
              ui.contractor.appMfy = next.mfyText;
              localStorage.setItem("user_info", JSON.stringify(ui));
            }
          } catch (e) { /* ignore */ }
          this.closeMyMahallaModal();
          this.loadMyMembers();
        })
        .catch((error) => {
          let msg = this.$t("MPSubmitError");
          if (error && error.response && error.response.data) {
            const d = error.response.data;
            if (typeof d === "string") msg = d;
            else if (d.title) msg = d.title;
            else if (d.errors) {
              const errors = Object.values(d.errors);
              msg = errors.flat ? errors.flat().join("; ") : errors.join("; ");
            }
          }
          this.myModal.error = typeof msg === "string" ? msg : this.$t("MPSubmitError");
        })
        .finally(() => {
          this.myModal.saving = false;
          this._saveMyMahallaInflight = false;
        });
    },
    loadMyMembers() {
      if (!this.myMahalla) {
        this.myMembers = [];
        return;
      }
      this.loadingMyMembers = true;
      const payload = {
        regionId: this.myMahalla.regionId,
        districtId: this.myMahalla.districtId,
        mfyId: this.myMahalla.mfyId,
        pinpp: "",
        search: "",
        sortBy: "",
        orderType: "",
        page: 1,
        pageSize: 100,
      };
      MahallaEmployeeService.GetList(payload)
        .then((res) => {
          const rows = (res.data && res.data.rows) || [];
          this.myMembers = rows;
        })
        .catch(() => {
          this.myMembers = [];
        })
        .finally(() => {
          this.loadingMyMembers = false;
        });
    },
    setTypeFilter(code) {
      if (this.activeFilter === code) return;
      this.activeFilter = code;
      this.loadTickets();
    },
    loadTickets() {
      this.loadingTickets = true;
      const payload = { ...this.ticketsFilter };
      if (this.activeFilter && this.activeFilter !== "all") {
        payload.typeId = this.typeCodeToId(this.activeFilter);
      }
      MahallaRequestService.GetList(payload)
        .then((res) => {
          const rows = (res.data && (res.data.rows || res.data.items)) || [];
          this.tickets = rows.map((r) => this.mapTicket(r));
        })
        .catch(() => {
          this.tickets = [];
        })
        .finally(() => {
          this.loadingTickets = false;
        });
    },
    mapTicket(item) {
      const idVal =
        item.requestNumber ||
        item.number ||
        item.code ||
        item.id ;
      const dateVal =
        item.docOn ||
        item.docDate ||
        item.dateOfCreated ||
        item.createDate ||
        item.createdDate ||
        item.createDateTime ||
        item.date ||
        "";
      const replyText =
        item.responseText || item.replyText || item.reply || item.answer || "";
      const filesRaw = item.files || item.attachments || [];
      const files = Array.isArray(filesRaw)
        ? filesRaw.map((f) => ({
            id: f.id || f.fileId || "",
            name: f.name || f.fileName || f.originalName || "",
          }))
        : [];
      const replyFilesRaw =
        item.responseFiles || item.replyFiles || item.answerFiles || [];
      const replyFiles = Array.isArray(replyFilesRaw)
        ? replyFilesRaw.map((f) => ({
            id: f.id || f.fileId || "",
            name: f.name || f.fileName || f.originalName || "",
          }))
        : [];
      const recipientName =
        item.mahallaEmployee ||
        item.toFullName ||
        item.recipientName ||
        item.employeeFullName ||
        item.mahallaEmployeeFullName ||
        item.person ||
        "";
      const recipientPosition =
        item.mahallaEmployeePositionName ||
        item.toPosition ||
        item.recipientPosition ||
        item.positionName ||
        item.employeePosition ||
        item.mahallaEmployeePosition ||
        "";
      return {
        id: idVal,
        rawId: item.id,
        type: this.mapTypeCode(
          item.typeId || item.type || item.requestTypeId || item.requestType || item.applicantTypeId
        ),
        typeName: item.typeName || "",
        date: this.formatTicketDate(dateVal),
        rawDate: dateVal,
        status: this.mapStatusCode(item.statusId || item.status),
        statusName: item.status || "",
        text: item.requestText || item.text || item.description || "",
        phoneNumber: item.phoneNumber || item.phone || "",
        person: recipientName,
        recipientName,
        recipientPosition,
        regionText: item.regionName || item.region || "",
        districtText: item.districtName || item.district || "",
        mfyText: item.mfyName || item.mfy || "",
        applicantTypeName: item.applicantTypeName || "",
        contractor: item.contractor || "",
        hasReply: !!replyText,
        replyText,
        files,
        replyFiles,
      };
    },
    mapTypeCode(t) {
      if (t == null) return "complaint";
      const v = String(t).toLowerCase();
      if (v === "1" || v === "complaint" || v === "shikoyat") return "complaint";
      if (v === "2" || v === "suggestion" || v === "taklif") return "suggestion";
      if (v === "3" || v === "application" || v === "ariza") return "application";
      return v;
    },
    mapStatusCode(s) {
      if (s == null) return "new";
      const v = String(s).toLowerCase();
      if (v === "1" || v === "new" || v === "yangi") return "new";
      if (
        v === "2" ||
        v === "inprogress" ||
        v === "in_progress" ||
        v === "jarayonda"
      )
        return "inProgress";
      if (v === "3" || v === "closed" || v === "yopildi" || v === "done")
        return "closed";
      return v;
    },
    formatTicketDate(d) {
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d);
      const day = dt.getDate().toString().padStart(2, "0");
      const mon = (dt.getMonth() + 1).toString().padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}.${mon}.${year}`;
    },
    drill(s) {
      this.drillStep = s;
      if (s === "v") this.searchRegion = "";
      if (s === "t") this.searchDistrict = "";
      if (s === "m") this.searchMahalla = "";
    },
    iconClass(index) {
      return this.regionIconClasses[index % this.regionIconClasses.length];
    },
    filterByText(list, query) {
      if (!query) return list;
      const q = query.trim().toLowerCase();
      if (!q) return list;
      return list.filter((item) => (item.text || "").toLowerCase().includes(q));
    },
    loadRegions() {
      this.loadingRegions = true;
      ManualService.RegionSelectList()
        .then((res) => {
          this.regions = res.data || [];
        })
        .finally(() => {
          this.loadingRegions = false;
        });
    },
    selectRegion(r) {
      this.selectedRegion = r;
      this.selectedDistrict = null;
      this.selectedMahalla = null;
      this.districts = [];
      this.mahallas = [];
      this.searchDistrict = "";
      this.drillStep = "t";
      this.loadingDistricts = true;
      ManualService.DistrictSelectList(r.value)
        .then((res) => {
          this.districts = res.data || [];
        })
        .finally(() => {
          this.loadingDistricts = false;
        });
    },
    selectDistrict(d) {
      this.selectedDistrict = d;
      this.selectedMahalla = null;
      this.mahallas = [];
      this.searchMahalla = "";
      this.drillStep = "m";
      this.loadingMahallas = true;
      ManualService.MfySelectList(this.selectedRegion.value, d.value)
        .then((res) => {
          this.mahallas = res.data || [];
        })
        .finally(() => {
          this.loadingMahallas = false;
        });
    },
    selectMahalla(m) {
      this.selectedMahalla = m;
      this.drillStep = "mem";
      this.loadMembers();
    },
    syncMfyEmployees(m) {
      if (!m || !m.value) return;
      if (this.syncingMfyId) return;
      this.syncingMfyId = m.value;
      MahallaEmployeeService.SyncMfyEmployees(m.value)
        .then(() => {
          this.$toast.open({
            message: this.$t("MPSyncSuccess") || "Sinxronlash muvaffaqiyatli bajarildi",
            type: "success",
            duration: 4000,
            dismissible: true,
          });
          if (this.selectedMahalla && this.selectedMahalla.value === m.value) {
            this.loadMembers();
          }
          if (this.myMahalla && this.myMahalla.mfyId === m.value) {
            this.loadMyMembers();
          }
        })
        .catch((error) => {
          let msg = this.$t("MPSyncError") || "Sinxronlashda xatolik";
          if (error && error.response && error.response.data) {
            const d = error.response.data;
            if (typeof d === "string") msg = d;
            else if (d.title) msg = d.title;
            else if (d.message) msg = d.message;
          }
          this.$toast.open({
            message: msg,
            type: "error",
            duration: 5000,
            dismissible: true,
          });
        })
        .finally(() => {
          this.syncingMfyId = 0;
        });
    },
    loadMembers() {
      this.memMembers = [];
      this.loadingMembers = true;
      const payload = {
        regionId: this.selectedRegion ? this.selectedRegion.value : 0,
        districtId: this.selectedDistrict ? this.selectedDistrict.value : 0,
        mfyId: this.selectedMahalla ? this.selectedMahalla.value : 0,
        pinpp: "",
        search: "",
        sortBy: "",
        orderType: "",
        page: 1,
        pageSize: 100,
      };
      MahallaEmployeeService.GetList(payload)
        .then((res) => {
          const rows = (res.data && res.data.rows) || [];
          this.memMembers = rows;
        })
        .finally(() => {
          this.loadingMembers = false;
        });
    },
    onPhotoError(e) {
      e.target.src = this.emptyAvatar;
    },
    typeLabel(t) {
      if (t === "complaint") return this.$t("MPTypeComplaint");
      if (t === "request") return this.$t("MPTypeRequest");
      if (t === "suggestion") return this.$t("MPTypeSuggestion");
      if (t === "application") return this.$t("MPTypeApplication");
      return t;
    },
    openChat(name, role, ctx) {
      ctx = ctx || {};
      this.chatRecipient = {
        name,
        role,
        photo: "",
        employeeId: ctx.employeeId || 0,
        regionId: ctx.regionId || 0,
        districtId: ctx.districtId || 0,
        mfyId: ctx.mfyId || 0,
        regionText: ctx.regionText || "",
        districtText: ctx.districtText || "",
        mfyText: ctx.mfyText || "",
      };
      this.chatReturnTab = this.activeTab;
      this.chatOpen = true;
      this.resetChat();
    },
    closeChat() {
      this.chatOpen = false;
      this.activeTab = this.chatReturnTab || "all";
    },
    resetChat() {
      this.chatState = {
        type: null,
        phone: "",
        text: "",
        fileName: "",
        fileId: "",
        step: "type",
      };
      this.chatMessages = [{ kind: "welcome", time: this.nowTime() }];
      this.scrollChatBottom();
    },
    selectType(code) {
      this.chatState.type = code;
      this.chatState.step = "phone";
      this.chatInputText = "998";
      this.addTextMsg(this.typeLabel(code), true);
      setTimeout(() => {
        this.addTextMsg(this.$t("MPAskPhone"), false);
        this.$nextTick(() => {
          const el = this.$refs.chatInput;
          if (el) {
            el.focus();
            const pos = (this.chatInputText || "").length;
            try { el.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
          }
        });
      }, 400);
    },
    chatSend() {
      const txt = this.chatInputText.trim();
      if (!txt) return;
      this.chatInputText = "";
      if (this.$refs.chatInput) this.$refs.chatInput.style.height = "";
      const step = this.chatState.step;
      if (step === "phone") {
        const digits = txt.replace(/\D/g, "");
        if (!/^998\d{9}$/.test(digits)) {
          this.addTextMsg(txt, true);
          setTimeout(() => this.addTextMsg(this.$t("MPPhoneInvalid"), false), 300);
          this.chatInputText = "998";
          return;
        }
        this.chatState.phone = digits;
        this.addTextMsg(digits, true);
        this.chatState.step = "text";
        setTimeout(() => this.addTextMsg(this.$t("MPWriteText"), false), 400);
      } else if (step === "text") {
        this.chatState.text = txt;
        this.addTextMsg(txt, true);
        this.chatState.step = "file";
        setTimeout(() => {
          this.chatMessages.push({ kind: "fileConfirm", time: this.nowTime() });
          this.scrollChatBottom();
        }, 400);
      } else if (step === "done") {
        this.addTextMsg(txt, true);
        setTimeout(() => this.addTextMsg(this.$t("MPMessageAccepted"), false), 500);
      }
    },
    showConfirmSummary() {
      this.chatMessages.push({
        kind: "confirmInfo",
        type: this.chatState.type,
        phone: this.chatState.phone,
        text: this.chatState.text,
        fileName: this.chatState.fileName,
        time: this.nowTime(),
      });
      this.scrollChatBottom();
      setTimeout(() => {
        this.chatMessages.push({ kind: "confirm" });
        this.scrollChatBottom();
      }, 300);
      this.chatState.step = "confirm";
    },
    typeCodeToId(code) {
      switch (code) {
        case "complaint": return 1;
        case "suggestion": return 2;
        case "application": return 3;
        default: return 1;
      }
    },
    buildRequestPayload() {
      let userInfo = {};
      try {
        userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
      } catch (e) { userInfo = {}; }
      const contractorId = userInfo.contractorId || 0;
      const senderContractor = userInfo.contractor || {};
      const senderRegionId = senderContractor.regionId || 0;
      const applicantTypeId = this.typeCodeToId(this.chatState.type);
      const files = this.chatState.fileId ? [{ id: this.chatState.fileId }] : [];
      return {
        applicantTypeId,
        typeId: applicantTypeId,
        text: this.chatState.text || "",
        phoneNumber: this.chatState.phone,
        contractorId,
        mahallaEmployeeId: this.chatRecipient.employeeId || 0,
        regionId: this.chatRecipient.regionId || 0,
        districtId: this.chatRecipient.districtId || 0,
        mfyId: this.chatRecipient.mfyId || 0,
        responsibleRegionId: this.chatRecipient.regionId || 0,
        files,
      };
    },
    confirmSend() {
      if (this.sendingRequest) return;
      this.chatMessages = this.chatMessages.filter((m) => m.kind !== "confirm");
      this.addTextMsg(this.$t("MPYesSend"), true);
      this.addSysMsg(this.$t("MPSent"));
      this.sendingRequest = true;
      const payload = this.buildRequestPayload();
      MahallaRequestService.Create(payload)
        .then((res) => {
          const data = (res && res.data) || {};
          const id =
            data.requestNumber ||
            data.number ||
            data.code ||
            (data.id != null ? "MRJ-" + data.id : "MRJ-" + Math.floor(100000 + Math.random() * 900000));
          const ds = this.formatTicketDate(
            data.createDate || data.createdDate || new Date().toISOString()
          );
          this.chatMessages.push({
            kind: "ticket",
            id,
            type: this.chatState.type,
            date: ds,
            time: this.nowTime(),
          });
          this.loadTickets();
          setTimeout(() => {
            this.addTextMsg(this.$t("MPReplyHint"), false);
            this.chatMessages.push({ kind: "newbtn" });
            this.chatState.step = "done";
            this.scrollChatBottom();
          }, 400);
        })
        .catch((error) => {
          let msg = this.$t("MPSubmitError");
          if (error && error.response && error.response.data) {
            const d = error.response.data;
            if (typeof d === "string") msg = d;
            else if (d.title) msg = d.title;
            else if (d.errors) {
              const errors = Object.values(d.errors);
              msg = errors.flat ? errors.flat().join("; ") : errors.join("; ");
            }
          }
          this.addSysMsg(typeof msg === "string" ? msg : this.$t("MPSubmitError"));
          this.chatMessages.push({ kind: "confirm" });
          this.scrollChatBottom();
        })
        .finally(() => {
          this.sendingRequest = false;
        });
    },
    cancelConfirm() {
      this.chatMessages = this.chatMessages.filter((m) => m.kind !== "confirm");
      this.chatState.step = "done";
      this.addTextMsg(this.$t("MPCancelled"), false);
      this.chatMessages.push({ kind: "newbtn" });
      this.scrollChatBottom();
    },
    confirmFileUpload() {
      this.chatMessages = this.chatMessages.filter((m) => m.kind !== "fileConfirm");
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
        this.$refs.fileInput.click();
      }
    },
    cancelFileUpload() {
      this.chatMessages = this.chatMessages.filter((m) => m.kind !== "fileConfirm");
      if (this.chatState.step === "file") {
        this.addTextMsg(this.$t("MPFileSkipped"), false);
        setTimeout(() => this.showConfirmSummary(), 300);
      } else {
        this.addTextMsg(this.$t("MPFileCancelled"), false);
      }
    },
    onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      e.target.value = "";
      if (file.size > 10 * 1024 * 1024) {
        this.addTextMsg(this.$t("MPFileTooLarge"), false);
        if (this.chatState.step === "file") this.repromptFileUpload();
        return;
      }
      const fileSize = this.formatFileSize(file.size);
      this.chatMessages.push({
        kind: "file",
        fileName: file.name,
        fileSize,
        time: this.nowTime(),
      });
      this.addSysMsg(this.$t("MPFileUploading"));
      this.scrollChatBottom();

      MahallaRequestService.UploadFile(file)
        .then((res) => {
          const data = res.data;
          let fileId = "";
          if (data && typeof data === "object") {
            fileId = data.id || data.fileId || (Array.isArray(data) && data[0] && (data[0].id || data[0].fileId)) || "";
          } else if (typeof data === "string") {
            fileId = data;
          }
          if (this.chatState.step === "file") {
            this.chatState.fileName = file.name;
            this.chatState.fileId = fileId;
            this.addSysMsg(this.$t("MPFileSent"));
            setTimeout(() => this.showConfirmSummary(), 400);
          } else {
            this.addSysMsg(this.$t("MPFileSent"));
          }
        })
        .catch(() => {
          this.addSysMsg(this.$t("MPFileError"));
          if (this.chatState.step === "file") this.repromptFileUpload();
        });
    },
    repromptFileUpload() {
      this.chatMessages = this.chatMessages.filter((m) => m.kind !== "fileConfirm");
      setTimeout(() => {
        this.chatMessages.push({ kind: "fileConfirm", time: this.nowTime() });
        this.scrollChatBottom();
      }, 400);
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    addTextMsg(text, isMe) {
      this.chatMessages.push({ kind: "text", text, me: isMe, time: this.nowTime() });
      this.scrollChatBottom();
    },
    addSysMsg(text) {
      this.chatMessages.push({ kind: "sys", text });
      this.scrollChatBottom();
    },
    scrollChatBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatBody;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
    chatKey(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.chatSend();
      }
    },
    onChatInput(e) {
      if (this.chatState.step === "phone") {
        const filtered = (this.chatInputText || "").replace(/\D/g, "").slice(0, 12);
        if (filtered !== this.chatInputText) {
          this.chatInputText = filtered;
        }
      }
      this.autoH(e);
    },
    autoH(e) {
      const el = e.target;
      el.style.height = "";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    },
    openTicketDetail(t) {
      this.ticketDetail = t;
      if (t.rawId == null) return;
      this.loadingTicketDetail = true;
      MahallaRequestService.Get(t.rawId)
        .then((res) => {
          const data = res.data || {};
          this.ticketDetail = this.mapTicket(data);
        })
        .catch(() => {})
        .finally(() => {
          this.loadingTicketDetail = false;
        });
    },
    closeTicketDetail() {
      this.ticketDetail = null;
      this.loadingTicketDetail = false;
    },
    ticketLocation(t) {
      if (!t) return "";
      const parts = [];
      if (t.regionText) parts.push(t.regionText);
      if (t.districtText) parts.push(t.districtText);
      if (t.mfyText) parts.push(t.mfyText);
      return parts.join(", ");
    },
    downloadFile(file) {
      if (!file || !file.id) return;
      MahallaRequestService.DownloadFile(file.id)
        .then((res) => {
          const data = res && res.data;
          if (!data) return;
          const url = URL.createObjectURL(data);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name || "file";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 1500);
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>

